import * as yup from 'yup';
import { RendererType } from '../../../data-models/field-definition';
import { IFormFieldSelectMeta } from '../../../view-models/form.view-model';
import { requiredMsg } from '../../Finance/components/TransactionModal/Forms/utils/validationMessages';
import {
  FieldEntity,
  FundReportingGridRef,
  GridRef,
  ISimpleChoice,
} from '../../../data-models/field2.data-model';
import { PermissionService } from '../../../services/PermissionService';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { getForesightStore } from '../../../util/jotai-store';
import { currencyMapByCodeAtom } from '../../../services/state/AppConfigStateJ';

export const RendererTypeOptions: Partial<Record<RendererType, string>> = {
  [RendererType.text]: 'Text',
  [RendererType.singleSelect]: 'Select',
  [RendererType.multiSelect]: 'Multiselect',
  [RendererType.number]: 'Number',
  [RendererType.currency]: 'Currency',
  [RendererType.percent]: 'Percent',
  [RendererType.date]: 'Date',
  [RendererType.colorStatus]: 'Color Status',
};

export const FieldEntityOptions: Partial<Record<FieldEntity, string>> = {
  [FieldEntity.company]: 'Company',
  [FieldEntity.fundCompany]: 'Company & Fund',
  [FieldEntity.deal]: 'Deal',
  [FieldEntity.fund]: 'Fund',
  [FieldEntity.round]: 'Round',
  [FieldEntity.captableInvestment]: 'Security',
};

export function customFieldViewModelFields() {
  return {
    rendererType: yup
      .string()
      .oneOf(Object.values(RendererType))
      .default(RendererType.text)
      .label('Type')
      .required(),
    currencyConfig: yup.string().nullable().optional().label('Currency'),
    /**
     * The chosen display format, stored as `formMeta.formatter.id`. Absent means the field keeps
     * whatever its Type derives. Its options depend on the chosen Type, so the field itself is
     * built by `useFormatterFormField` rather than declared here.
     */
    formatterId: yup.string().nullable().optional().default(undefined).label('Format'),
    usePresetOptions: yup
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .label('Restrict to predefined options')
      .customMeta({
        description: 'When enabled, users can only select from the options you define below.',
      }),
    selectConfigValues: yup
      .array()
      .of(yup.string())
      .nullable()
      .optional()
      .default(undefined)
      .label('Options'),
    cleanupRemovedOptions: yup.boolean().nullable().optional().default(true),

    // gridRefs (conditional on entity)
    [FundReportingGridRef]: yup
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .label('Add a column to Fund Views'),
    [GridRef.kpiDataCollection]: yup
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .label('Add a column to KPI Company Grid'),
    [GridRef.portfolioReporting]: yup
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .label('Add a column to Company Views'),
    [GridRef.roundTracker]: yup
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .label('Add a column to Round Views'),
    [GridRef.securityOverview]: yup
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .label('Add a column to Security Views'),
    // formRefs (also conditional on entity)
    addingCaptableSecurity: yup
      .boolean()
      .nullable()
      .optional()
      .default(false)
      .label('Adding Captable Security'),
    addingTransaction: yup.boolean().nullable().optional().default(false).label('Adding Transaction'),
    addingDeal: yup.boolean().nullable().optional().default(false).label('Adding Deal'),
    addingRound: yup.boolean().nullable().optional().default(false).label('Adding Round'),
    companyLists: yup.boolean().nullable().optional().default(false).label('Company Lists'),
    companyProfile: yup.boolean().nullable().optional().default(false).label('Company Profile').customMeta({
      description: 'You can reorder and customize fields displayed later in Data Display',
    }),
  };
}

export function customFieldViewModelFormSchema(allowFreeFormMultiSelect = false) {
  const { rendererType, selectConfigValues, currencyConfig, ...baseFields } = customFieldViewModelFields();
  const currencyMapByCode = getForesightStore().get(currencyMapByCodeAtom);
  return yup.object({
    ...baseFields,
    rendererType: rendererType.formMeta({
      renderer: RendererType.singleSelect,
      rendererMeta: {
        values: Object.keys(RendererTypeOptions).map((value) => ({
          displayName: RendererTypeOptions[value as keyof typeof RendererTypeOptions] ?? value,
          value,
        })),
      },
    }),
    //formMeta.formatter.config.currency (the value is currency.code)
    currencyConfig: currencyConfig
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: [...currencyMapByCode.keys()].map((code) => ({
            displayName: code,
            value: code,
          })),
        },
      })
      .when('rendererType', {
        is: RendererType.currency,
        then: (schema) => schema.required(),
      })
      .formMeta({
        renderer: RendererType.singleSelect,
      }),
    // formMeta.renderer.config.values
    selectConfigValues: selectConfigValues
      .test('rendererType', requiredMsg('Options'), function (value, ctx) {
        const rendererType = ctx.parent.rendererType;
        const hasValues = value != null && value.length > 0;
        if (rendererType === RendererType.singleSelect) {
          return hasValues;
        }
        if (rendererType === RendererType.multiSelect) {
          if (!allowFreeFormMultiSelect || ctx.parent.usePresetOptions) {
            return hasValues;
          }
        }
        return true;
      })
      .formMeta<IFormFieldSelectMeta<string>>({
        renderer: RendererType.multiSelect,
        rendererMeta: {
          values: [],
          allowCustomAdd: true,
        },
      }),
  });
}

export type CustomFieldFormViewModel = yup.InferType<ReturnType<typeof customFieldViewModelFormSchema>>;

export function customFieldFields() {
  if (!PermissionService.get().hasPermission(PermissionKey.canViewDeal)) {
    delete FieldEntityOptions[FieldEntity.deal];
  }
  return {
    description: yup.string().label('Description').required(),
    displayName: yup.string().label('Name').required(),
    entity: yup.string().label('Entity').required().default(FieldEntity.company),
    entityField: yup.string().label('Entity Field').optional(),
    formMeta: yup.object().nullable().optional(),
    gridMeta: yup.object().nullable().optional(),
    id: yup.number(),
    systemMeta: yup.object().nullable().optional(),
    timeSeries: yup.boolean().nullable().default(false).label('Support time-based data'),
    type: yup.string(),
    _viewModel: yup.object(customFieldViewModelFields()),
  };
}

export function customFieldFormSchema(showSecurityView = false, allowFreeFormMultiSelect = false) {
  const { entity, ...baseFields } = customFieldFields();
  return yup.object({
    ...baseFields,
    entity: entity.formMeta<IFormFieldSelectMeta<string>>({
      label: 'Entity',
      renderer: RendererType.singleSelect,
      rendererMeta: {
        values: Object.entries(FieldEntityOptions).reduce((acc, [key, value]) => {
          if (key === FieldEntity.captableInvestment && !showSecurityView) {
            return acc;
          }
          acc.push({ displayName: value, value: key });
          return acc;
        }, [] as ISimpleChoice<string>[]),
      },
    }),
    _viewModel: customFieldViewModelFormSchema(allowFreeFormMultiSelect),
  });
}

export type CustomFieldFormType = yup.InferType<ReturnType<typeof customFieldFormSchema>>;
